import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ImportAdapter, ImportedItem, VaultItem } from '@heybesafe/shared';
import { effectiveWebIdentifiers, tryParseUrl, fillMissingUrlsFromNames, detectImportAdapter } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import BulkEntryTable from './BulkEntryTable';

type Step = 'source' | 'capture' | 'bulk' | 'review' | 'done';

interface ReviewRow {
  item: ImportedItem;
  selected: boolean;
  /** True when the imported item matches an existing vault item by
   *  hostname + username + password. Such rows render disabled with a
   *  "Duplicate" badge and are excluded from selection toggles + import. */
  isDuplicate: boolean;
}

interface Props {
  adapters: ImportAdapter[];
}

/**
 * Builds a hostname + username + password key for duplicate detection. The
 * URL side is hostname-normalised (lowercased, www. stripped, scheme-tolerant
 * via tryParseUrl) so an imported `https://example.com/login` with username
 * "alice" and password "p" deduplicates against a saved
 * `https://www.example.com` carrying the same credentials. Empty host is its
 * own bucket — items with no URL that share a username + password are still
 * considered duplicates.
 */
function dedupeKey(item: Pick<VaultItem, 'url' | 'username' | 'password' | 'identifiers' | 'type' | 'rpId'>): string {
  const url = effectiveWebIdentifiers(item)[0] ?? item.url ?? '';
  const host = tryParseUrl(url)?.hostname.toLowerCase().replace(/^www\./, '') ?? '';
  const username = (item.username ?? '').trim().toLowerCase();
  const password = item.password ?? '';
  return `${host}|${username}|${password}`;
}

export default function ImportWizard({ adapters }: Props) {
  const { addItem, items } = useSafe();
  const navigate = useNavigate();

  // Recomputed when the vault list changes — covers the edge case of a sync
  // landing between source-pick and capture (rare; the wizard is short-lived).
  const existingKeys = useMemo(() => new Set(items.map(dedupeKey)), [items]);
  const [step, setStep] = useState<Step>('source');
  const [adapter, setAdapter] = useState<ImportAdapter | null>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [captureError, setCaptureError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [skippedNoPassword, setSkippedNoPassword] = useState(0);
  const [skippedDuplicate, setSkippedDuplicate] = useState(0);
  // Drag-and-drop on the source step: which target is hovered (an adapter id, or
  // '__zone__' for the auto-detect background), and any drop error to surface.
  const [dragTarget, setDragTarget] = useState<string | null>(null);
  const [sourceError, setSourceError] = useState('');

  const STEP_LABELS: Record<Step, string> = {
    source: 'Choose a source',
    capture: 'Upload your data',
    bulk: 'Type your logins',
    review: 'Review',
    done: 'Done',
  };

  function handleSelectAdapter(a: ImportAdapter) {
    setAdapter(a);
    setCaptureError('');
    setStep('capture');
  }

  /**
   * Run the shared capture pipeline on already-parsed items (URL inference +
   * duplicate marking) and advance to review. Items may come from one file or
   * several (same or mixed sources) — they're all deduped together.
   */
  function showReview(parsedItems: ImportedItem[]) {
    setSourceError('');
    setCaptureError('');
    // For files that are "just names" (no URL column), infer a primary URL
    // from each item's name via the on-device brand→domain lookup. Items that
    // already carry a URL are left untouched.
    const parsed = fillMissingUrlsFromNames(parsedItems);
    // Seed the seen-set with the existing safe, then accumulate as we walk the
    // batch so intra-file AND cross-file duplicates (a key repeated within this
    // import — common in GPM exports, or across two overlapping exports) are
    // caught too: the first occurrence imports, later identical rows are marked
    // duplicate and left unchecked.
    const seen = new Set(existingKeys);
    setRows(parsed.map(item => {
      const key = dedupeKey(item);
      const isDuplicate = seen.has(key);
      seen.add(key);
      return { item, selected: !isDuplicate, isDuplicate };
    }));
    setStep('review');
  }

  /** Parse one or more files' texts with a single adapter, then review. */
  function runCaptureTexts(a: ImportAdapter, texts: string[]) {
    setAdapter(a);
    showReview(texts.flatMap(t => a.parse(t)));
  }

  function handleCapture(rawInput: string | string[]) {
    if (!adapter) return;
    runCaptureTexts(adapter, Array.isArray(rawInput) ? rawInput : [rawInput]);
  }

  /** Read every dropped file as text, or null (with a surfaced error) if any
   *  isn't a CSV or can't be read. */
  async function readDroppedCsvs(e: React.DragEvent): Promise<string[] | null> {
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) { setSourceError('Drop one or more files exported from your old password manager.'); return null; }
    const bad = files.find(f => !(/\.csv$/i.test(f.name) || /csv|text\//i.test(f.type)));
    if (bad) { setSourceError(`“${bad.name}” isn’t a .csv file. Export your passwords as CSV and try again.`); return null; }
    try {
      return await Promise.all(files.map(f => f.text()));
    } catch {
      setSourceError('Could not read that file. Please try again.');
      return null;
    }
  }

  /** Drop onto a specific source tile → parse every dropped file with that adapter. */
  async function handleAdapterDrop(a: ImportAdapter, e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation(); // don't also trigger the auto-detect zone
    setDragTarget(null);
    const texts = await readDroppedCsvs(e);
    if (texts) runCaptureTexts(a, texts);
  }

  /** Drop on the background (not a tile) → auto-detect each file's source. */
  async function handleZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragTarget(null);
    const texts = await readDroppedCsvs(e);
    if (!texts) return;
    // Detect per file, so a single drop can mix sources (e.g. a Chrome export +
    // a LastPass export). Parse each recognized file with its own adapter.
    const detections = texts.map(t => ({ text: t, adapter: detectImportAdapter(t, adapters) }));
    const items = detections.flatMap(d => (d.adapter ? d.adapter.parse(d.text) : []));
    const recognized = detections.filter(d => d.adapter !== null).length;
    if (recognized === 0) {
      setSourceError(
        "Couldn’t recognize that file automatically. Drop it on your password manager above, or use Spreadsheet to map the columns.",
      );
      return;
    }
    showReview(items);
  }

  function toggleRow(i: number) {
    setRows(prev => prev.map((r, idx) =>
      idx === i && !r.isDuplicate ? { ...r, selected: !r.selected } : r,
    ));
  }

  function toggleAll() {
    // Duplicates stay unselected regardless — the toggle only affects rows
    // the user could actually import.
    const eligible = rows.filter(r => !r.isDuplicate);
    if (eligible.length === 0) return;
    const allSelected = eligible.every(r => r.selected);
    setRows(prev => prev.map(r => (r.isDuplicate ? r : { ...r, selected: !allSelected })));
  }

  function updateField(rowIdx: number, field: keyof ImportedItem, value: string) {
    setRows(prev => prev.map((r, i) =>
      i === rowIdx ? { ...r, item: { ...r.item, [field]: value || undefined } } : r
    ));
  }

  async function handleImport() {
    setImporting(true);
    const selected = rows.filter(r => r.selected);
    let count = 0;
    for (const { item } of selected) {
      try { await addItem(item); count++; } catch { /* continue */ }
    }
    setImportedCount(count);
    setImporting(false);
    setStep('done');
  }

  async function handleBulkSave(items: ImportedItem[]) {
    setImporting(true);
    let saved = 0;
    let noPassword = 0;
    let duplicate = 0;
    // Seed with the existing safe, then grow so repeated rows within this same
    // batch also count as duplicates (first wins, rest skipped).
    const seen = new Set(existingKeys);
    for (const item of items) {
      if (!item.password?.trim()) { noPassword++; continue; }
      const key = dedupeKey(item);
      if (seen.has(key)) { duplicate++; continue; }
      seen.add(key);
      try { await addItem(item); saved++; } catch { /* continue */ }
    }
    setImportedCount(saved);
    setSkippedNoPassword(noPassword);
    setSkippedDuplicate(duplicate);
    setImporting(false);
    setStep('done');
  }

  const selectedCount = rows.filter(r => r.selected).length;

  return (
    <div className="flex flex-col h-full">
      {/* Step indicator */}
      <div className="px-6 py-4 border-b border-hbs-border">
        <h1 className="font-display font-semibold text-[22px] tracking-hbs-tight text-hbs-text">Import passwords</h1>
        <p className="text-sm text-hbs-text-mute mt-0.5">{STEP_LABELS[step]}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">

        {/* Step 1: Source */}
        {step === 'source' && (
          <div
            // The whole grid is an auto-detect drop zone: a CSV dropped on the
            // background (not on a specific button) is sniffed and routed to the
            // matching adapter. Per-button drops stopPropagation, so they win.
            onDragOver={(e) => { e.preventDefault(); if (dragTarget === null) setDragTarget('__zone__'); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target && dragTarget === '__zone__') setDragTarget(null); }}
            onDrop={handleZoneDrop}
            className={`rounded-hbs-xl transition-colors ${dragTarget === '__zone__' ? 'ring-2 ring-hbs-primary ring-offset-2 ring-offset-hbs-bg' : ''}`}
          >
            <p className="text-xs text-hbs-text-mute mb-3">
              Pick where your passwords are coming from — or drag one or more CSV exports onto a
              tile. Drop them anywhere and we’ll try to detect the format.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {adapters.map(a => {
                // Only adapters that parse raw CSV (those with a `detect` sniff)
                // are drop targets. The spreadsheet mapper needs its column-mapping
                // UI, so a file dropped on its tile bubbles to the auto-detect zone.
                const droppable = typeof a.detect === 'function';
                return (
                <button
                  key={a.id}
                  id={`import-adapter-${a.id}`}
                  onClick={() => handleSelectAdapter(a)}
                  onDragOver={droppable ? (e) => { e.preventDefault(); e.stopPropagation(); setDragTarget(a.id); } : undefined}
                  onDragLeave={droppable ? (e) => { e.stopPropagation(); setDragTarget((t) => (t === a.id ? null : t)); } : undefined}
                  onDrop={droppable ? (e) => { void handleAdapterDrop(a, e); } : undefined}
                  className={`flex flex-col items-center gap-3 p-6 rounded-hbs-xl border transition-colors text-left ${
                    dragTarget === a.id
                      ? 'bg-hbs-surface-alt border-hbs-primary ring-2 ring-hbs-primary'
                      : 'bg-hbs-surface hover:bg-hbs-surface-alt border-hbs-border'
                  }`}
                >
                  <span className="text-sm font-semibold text-hbs-text">{a.name}</span>
                  <span className="text-xs text-hbs-text-mute text-center">
                    {dragTarget === a.id ? `Drop to import from ${a.name}` : a.description}
                  </span>
                </button>
                );
              })}
              <button
                id="import-bulk-entry-button"
                onClick={() => { setCaptureError(''); setSourceError(''); setStep('bulk'); }}
                className="flex flex-col items-center gap-3 p-6 bg-hbs-surface hover:bg-hbs-surface-alt rounded-hbs-xl border border-hbs-border transition-colors text-left"
              >
                <span className="text-sm font-semibold text-hbs-text">Bulk entry</span>
                <span className="text-xs text-hbs-text-mute text-center">Type your logins into a table</span>
              </button>
            </div>
            {sourceError && (
              <p className="text-red-400 text-sm bg-red-400/10 rounded-hbs-md p-3 mt-4" role="alert">{sourceError}</p>
            )}
          </div>
        )}

        {/* Step 2: Capture */}
        {step === 'capture' && adapter && (
          <div className="flex flex-col gap-4">
            <button
            id="import-back-to-source-button"
            onClick={() => setStep('source')}
              className="self-start text-xs text-hbs-text-mute hover:text-hbs-text flex items-center gap-1"
            >
              ← Back
            </button>
            <adapter.CaptureComponent
              onCapture={handleCapture}
              onError={msg => setCaptureError(msg)}
            />
            {captureError && (
              <p className="text-red-400 text-sm bg-red-400/10 rounded-hbs-md p-3">{captureError}</p>
            )}
          </div>
        )}

        {step === 'bulk' && (
          <BulkEntryTable
            onSave={handleBulkSave}
            saving={importing}
            onBack={() => setStep('source')}
          />
        )}

        {/* Step 3: Review */}
        {step === 'review' && (
          <div className="flex flex-col gap-4">
            {rows.length === 0 ? (
              <div className="text-center py-12 text-hbs-text-mute text-sm">
                No items could be parsed from the input. Go back and try again, or check your file format.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <button id="import-toggle-all-button"
              onClick={toggleAll} className="text-xs text-hbs-primary hover:text-hbs-primary">
                    {rows.filter(r => !r.isDuplicate).every(r => r.selected) ? 'Deselect all' : 'Select all'}
                  </button>
                  <span className="text-xs text-hbs-text-mute">
                    {selectedCount} of {rows.length} selected
                    {rows.some(r => r.isDuplicate) && (
                      <> · {rows.filter(r => r.isDuplicate).length} duplicate{rows.filter(r => r.isDuplicate).length !== 1 ? 's' : ''} skipped</>
                    )}
                  </span>
                </div>

                <div className="border border-hbs-border rounded-hbs-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-hbs-surface border-b border-hbs-border">
                      <tr>
                        <th className="w-8 px-3 py-2"></th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-hbs-text-mute">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-hbs-text-mute">Username</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-hbs-text-mute">Password</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-hbs-text-mute">URL</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-hbs-text-mute">Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}
                          className={`border-b border-hbs-border last:border-0 ${
                            row.isDuplicate ? 'bg-ink-secondary/5 opacity-60' :
                            !row.item.name ? 'bg-amber-400/5' : ''
                          }`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              disabled={row.isDuplicate}
                              onChange={() => toggleRow(i)}
                              title={row.isDuplicate ? 'Duplicate of an existing login — cannot be imported' : undefined}
                              className="rounded border-hbs-border disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-hbs-text px-1">{row.item.name}</span>
                              {row.isDuplicate && (
                                <span
                                  title="An existing login already has this URL, username, and password"
                                  className="text-[10px] font-semibold uppercase tracking-wide text-hbs-text-mute bg-hbs-surface-alt border border-hbs-border rounded px-1.5 py-0.5 whitespace-nowrap"
                                >
                                  Duplicate
                                </span>
                              )}
                            </div>
                          </td>
                          {(['username', 'password', 'url', 'group'] as const).map(field => (
                            <td key={field} className="px-2 py-1">
                              <input
                                type={field === 'password' ? 'password' : 'text'}
                                value={(row.item[field] as string) ?? ''}
                                onChange={e => updateField(i, field, e.target.value)}
                                disabled={row.isDuplicate}
                                className="w-full bg-transparent text-sm text-hbs-text focus:outline-none focus:ring-1 focus:ring-hbs-primary rounded px-1 py-0.5 min-w-[80px] disabled:cursor-not-allowed"
                                placeholder={field}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div className="flex flex-col items-center justify-center gap-6 py-16">
            <svg className="w-16 h-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex flex-col items-center gap-1">
              <p className="text-lg font-semibold text-hbs-text">
                {importedCount} login{importedCount !== 1 ? 's' : ''} saved.
              </p>
              {(skippedNoPassword > 0 || skippedDuplicate > 0) && (
                <p className="text-sm text-hbs-text-mute">
                  Skipped:{' '}
                  {[
                    skippedNoPassword > 0 ? `${skippedNoPassword} missing password` : null,
                    skippedDuplicate > 0 ? `${skippedDuplicate} duplicate` : null,
                  ].filter(Boolean).join(', ')}.
                </p>
              )}
            </div>
            <button
              id="import-go-to-safe-button"
              onClick={() => navigate('/safe')}
              className="px-6 py-2.5 bg-hbs-primary hover:bg-hbs-primary-hover text-white text-sm font-medium rounded-hbs-md transition-colors"
            >
              Go to safe
            </button>
          </div>
        )}

      </div>

      {/* Static bottom rail — the import action stays pinned below the
          scrolling review list instead of trailing off the end of it. */}
      {step === 'review' && rows.length > 0 && (
        <div className="border-t border-hbs-border px-6 py-4 flex justify-center">
          <button
              id="import-confirm-button"
              onClick={handleImport}
            disabled={selectedCount === 0 || importing}
            className="px-6 py-2.5 bg-hbs-primary hover:bg-hbs-primary-hover disabled:opacity-50 text-white text-sm font-medium rounded-hbs-md transition-colors"
          >
            {importing ? 'Importing…' : `Import ${selectedCount} item${selectedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}
